import React, { useEffect, useState } from 'react';
import ReviewOutput from '@app/model/review/output/ReviewOutput';
import { ChevronDownIcon, ChevronUpIcon, UserCircleIcon } from '@heroicons/react/20/solid';
import ReviewScoreTag from '@app/site/secured/review-manager/fragment/reviews-inbox/component/ReviewScoreTag';
import { findProviderData } from '@app/component/provider/FindProviderData';
import Tag from '@app/component/tag/Tag';
import Utilities from '@util/Utilities';
import ReviewStateState from '@app/model/review/ReviewStateState';
import { ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/outline';
import { useReviewsTools } from '@app/site/secured/review-manager/provider/useReviewsTools';
import ReviewMessageReplyFragment
	from '@app/site/secured/review-manager/fragment/reviews-inbox/ReviewMessageReplyFragment';
import ReviewViewMessageFragment
	from '@app/site/secured/review-manager/fragment/reviews-inbox/ReviewViewMessageFragment';
import useSecurity from '@util/SecurityHook';
import { ReviewService } from '@app/model/review/service/ReviewService';
import { useQueryClient } from '@tanstack/react-query';
import ReviewLogEntriesFragment from '@app/site/secured/review-manager/fragment/reviews-inbox/ReviewLogEntriesFragment';
import useModal from '@app/component/overlays/modal/ModalRef';
import ReviewMessageInternalCommentModal
	from '@app/site/secured/review-manager/fragment/reviews-inbox/modal/ReviewMessageInternalCommentModal';
import ReviewHandlingSessionLogEntry from '@app/model/review/ReviewHandlingSessionLogEntry';
import useDialogue from '@app/component/overlays/dialog/DialogueRef';
import Dialogue from '@app/component/overlays/dialog/Dialogue';
import useToaster from '@app/component/overlays/toaster/ToasterHook';


const ReviewHandleMessageFragment: React.FunctionComponent<{ reviewOutput: ReviewOutput }> = ({ reviewOutput }) => {

	const tools = useReviewsTools();
	const security = useSecurity();
	const queryClient = useQueryClient();
	const internalNoteModal = useModal();
	const removeNoteDialogue = useDialogue<number>();

	const [selectedLogEntry, setSelectedLogEntry] = useState<ReviewHandlingSessionLogEntry | undefined>(undefined);
	const [isInternalNotesListOpen, setIsInternalNotesOpen] = useState(false);

	const toaster = useToaster();

	useEffect(() => {
		if (!reviewOutput.hasSessionEntryViewedByUserId(security.principal!.id)) {
			ReviewService.markReviewAsViewedByUser(reviewOutput.review.id, tools.filter, queryClient);
		}
	}, [reviewOutput.review.id]);

	const onEditInternalNote = (logEntry: ReviewHandlingSessionLogEntry) => {
		setSelectedLogEntry(logEntry);
		internalNoteModal.open();
	};

	const openRemoveInternalNoteDialogue = (logEntry: ReviewHandlingSessionLogEntry) => {
		removeNoteDialogue.open(logEntry.id);
	};

	const onDeleteInternalNote = () => {
		if (!!removeNoteDialogue.getValue())
			ReviewService.removeReviewInternalNotes(reviewOutput.review.id, removeNoteDialogue.getValue()!, tools.filter, queryClient)
				.then(() => toaster.success('Review Management', 'Internal note deleted successfully!'))
				.catch(() => toaster.error('Review Management', 'Failed to delete internal note'));
	};

	return <>
		<ReviewMessageInternalCommentModal ref={internalNoteModal.ref} reviewOutput={reviewOutput}
																			 logEntry={selectedLogEntry} onClose={() => setSelectedLogEntry(undefined)} />
		<Dialogue ref={removeNoteDialogue.ref} onConfirm={onDeleteInternalNote} title="Delete internal note" cancelButtonName='Cancel' confirmButtonName='Yes, Delete'>
			<span className="text-sm">Are you sure you want to delete this entry?</span>
		</Dialogue>
		<section aria-labelledby="message-heading"
						 className="flex h-full min-w-0 flex-1 flex-col overflow-hidden shadow lg:shadow-none lg:border-r lg:border-l border-gray-200">
			{/* Top section */}
			<div className="flex-shrink-0 border-y border-gray-200 bg-white lg:border-t-0">
				{/* Toolbar*/}
				<div className="flex flex-col justify-center">
					<div className="px-4 sm:px-6 lg:px-8">
						<div className="flex flex-wrap justify-between py-4 gap-y-2">
							{/* Left buttons */}
							<div>
								<div className="inline-flex rounded-md shadow-sm space-x-3">
									<div className="inline-flex">
										<button type="button" onClick={() => setIsInternalNotesOpen(!isInternalNotesListOpen)}
														className="relative inline-flex items-center gap-x-1.5 rounded-l-md bg-white px-3 py-1.5 text-sm text-gray-700 ring-1 ring-inset ring-gray-300 hover:z-10 hover:bg-gray-50 focus:z-10">
											<ChatBubbleBottomCenterTextIcon className="-ml-0.5 h-4 w-4 text-gray-400" />
											{isInternalNotesListOpen ? 'Close Internal Notes' : 'Open Internal Notes'}
										</button>
										<button type="button"
														className="relative inline-flex -ml-px items-center gap-x-1.5 rounded-r-md bg-white px-3 py-1.5 text-sm text-gray-700 ring-1 ring-inset ring-gray-300 hover:z-10 hover:bg-gray-50 focus:z-10">
											<UserCircleIcon className="-ml-0.5 h-4 w-4 text-gray-400" />
											Assign
										</button>
									</div>
								</div>
							</div>

							{/* Right buttons */}
							<nav aria-label="Pagination">
								<span className="isolate inline-flex rounded-md shadow-sm">
										<a onClick={tools.prev}
											 className="relative inline-flex items-center rounded-l-md bg-white px-3 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:z-10 hover:bg-gray-50 hover:text-gray-700 focus:z-10 cursor-pointer">
												<span className="sr-only">Next</span>
												<ChevronUpIcon className="h-4 w-4" />
										</a>
										<a onClick={tools.next}
											 className="relative -ml-px inline-flex items-center rounded-r-md bg-white px-3 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:z-10 hover:bg-gray-50 hover:text-gray-700 focus:z-10 cursor-pointer">
												<span className="sr-only">Previous</span>
												<ChevronDownIcon className="h-4 w-4" />
										</a>
								</span>
							</nav>
						</div>
					</div>
				</div>
			</div>
			{/* Message */}
			<div className="min-h-0 flex-1 overflow-y-auto bg-gray-50 lg:border-b border-gray-200">
				{/* Heading */}
				<div className="bg-white pb-6 pt-5 border-b border-gray-200">
					<div className="px-4 sm:flex sm:items-baseline sm:justify-between sm:px-6 lg:px-8">
						<div className="sm:w-0 sm:flex-1">
							<div>
								<h2 className="flex flex-wrap items-center gap-y-2 gap-x-3">
									<span className="text-base font-medium leading-6 text-gray-900">{reviewOutput.hotelName}</span>
									{!!reviewOutput.review.customerInfo.name && <>
										<div className="h-4 w-px bg-gray-300 mx-1 hidden sm:block"></div>
										<span className="text-sm text-gray-500">{reviewOutput.review.customerInfo.name}</span></>}
									<div className="h-4 w-px bg-gray-300 mx-1 hidden sm:block"></div>
									{findProviderData(reviewOutput.review.provider).logo!('h-3.5 w-auto relative top-0.5')}
								</h2>
								<div className="flex flex-wrap items-center gap-y-2 gap-x-2 mt-2 -ml-0.5">
									<ReviewScoreTag score={reviewOutput.review.score} />
									<Tag state="gray">{reviewOutput.review.roomName}</Tag>
									<Tag state="gray">{reviewOutput.review.bookInfo.nights} Nights</Tag>
									<Tag state="gray">{Utilities.formatDateString(reviewOutput.review.reviewDate, 'DD MMM YYYY')}</Tag>
								</div>
							</div>
						</div>

						<div className="mt-4 flex items-center justify-between sm:ml-6 sm:mt-0 sm:flex-shrink-0 sm:justify-start">
							{reviewOutput.session && reviewOutput.session.state === ReviewStateState.ANSWERED &&
								<Tag state="green" extraClassName="!text-sm !px-3 !py-0.5 !rounded-full">Answered</Tag>}
							{(!reviewOutput.session || reviewOutput.session.state === ReviewStateState.UNANSWERED) &&
								<Tag state="warning" extraClassName="!text-sm !px-3 !py-0.5 !rounded-full">Unanswered</Tag>}
						</div>
					</div>
				</div>
				<div className="space-y-6 p-4 lg:p-6">
					{/* Message section*/}
					{reviewOutput.hasContent() && <ReviewViewMessageFragment reviewOutput={reviewOutput} />}
					{/* Reply section*/}
					<ReviewMessageReplyFragment reviewOutput={reviewOutput} />
				</div>
				{reviewOutput.hasSessionEntries() &&
					<ReviewLogEntriesFragment session={reviewOutput.session}
																		isOpen={isInternalNotesListOpen}
																		onClose={() => setIsInternalNotesOpen(false)}
																		onAddInternalNote={internalNoteModal.open}
																		onEditInternalNote={onEditInternalNote}
																		onRemoveInternalNote={openRemoveInternalNoteDialogue} />}
			</div>
		</section>
	</>;

};

export default ReviewHandleMessageFragment;